import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button, Modal } from '@mantine/core';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { useTheme } from '@contexts/ThemeContext';
import { withHexAlpha } from '@components/commons/tariff-badge-styles';
import { collapsePatchLogsByVersion } from './collapse';
import { PatchLogEntryCard } from './PatchLogEntryCard';
import { PatchLogLocaleSwitcher } from './PatchLogLocaleSwitcher';
import { formatPatchLogDate, formatPatchLogVersionLabel, resolvePatchLogCopy } from './i18n';
import type { PatchLogLocale, PatchLogPublic } from './types';
import './patch-logs.css';

/** Above the AppShell navbar (100), below Notifications (10001). */
export const PATCH_LOG_MODAL_Z_INDEX = 9000;

/** Slack before the container counts as scrolled to the bottom (sub-pixel rounding). */
const BOTTOM_TOLERANCE_PX = 8;

/** Same pair, and the same light/dark rule, the sidebar uses. */
const BRAND_LOGO = '/image/png/apex-logo.png';
const BRAND_LOGO_WHITE = '/image/png/apex-logo-white.png';

/** Alpha over the theme's card color: the page behind stays legible through the sheet. */
const SURFACE_ALPHA = 0.62;

/** Corner rounding of the sheet. */
const SURFACE_RADIUS = 16;

export interface PatchLogModalProps {
  opened: boolean;
  /**
   * Newest first. Several logs merge into one modal with a version line each,
   * and logs sharing a version collapse into a single line — a version the user
   * has already seen a heading for must not appear twice.
   */
  logs: PatchLogPublic[];
  locale: PatchLogLocale;
  /** Omit to hide the in-modal language switcher (e.g. a fixed-locale preview). */
  onLocaleChange?: (locale: PatchLogLocale) => void;
  /** Acknowledges every log shown. */
  onConfirm?: () => void;
  /** Omit to hide "Remind me later" (e.g. the history view or a backoffice preview). */
  onDefer?: () => void;
  confirming?: boolean;
  zIndex?: number;
}

/**
 * Presentational, blocking "What's New" modal, shaped as a receipt note: a
 * narrow translucent column with flat edges and soft corners that sits over the
 * page instead of replacing it. Owns nothing but scroll state, so the backoffice preview and
 * the history page can reuse it as-is.
 *
 * Deliberately a one-way door: no X, no Esc, no click-outside. The only exits
 * are Confirm (at the end of the content) and, if offered, Remind Later —
 * position is the read gate, so nothing may dismiss it early. Read-only
 * surfaces (the history page, a backoffice preview) render the entry cards
 * directly instead of reusing this shell.
 */
export const PatchLogModal = ({
  opened,
  logs,
  locale,
  onLocaleChange,
  onConfirm,
  onDefer,
  confirming = false,
  zIndex = PATCH_LOG_MODAL_Z_INDEX,
}: PatchLogModalProps) => {
  const { isDark, colors: themeColors } = useTheme();
  const t = useCallback((key: Parameters<typeof resolvePatchLogCopy>[1]) => resolvePatchLogCopy(locale, key), [locale]);

  // The scroll container is captured through a callback ref: Mantine mounts
  // the modal body a render after `opened` flips, so a plain ref would still
  // be null when the effect first runs.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  // One section per version, not per row: `logs` can carry the same version
  // more than once when a release was published in several passes.
  const visibleLogs = useMemo(() => collapsePatchLogsByVersion(logs), [logs]);

  const evaluateScroll = useCallback(() => {
    if (!scrollEl) {
      return;
    }
    const remaining = scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
    setShowScrollHint(remaining > BOTTOM_TOLERANCE_PX);
  }, [scrollEl]);

  // Re-measure on scroll and whenever the container or its content resizes
  // (viewport change, language switch, an image that had no size hint).
  useEffect(() => {
    if (!scrollEl) {
      return;
    }
    evaluateScroll();
    scrollEl.addEventListener('scroll', evaluateScroll, { passive: true });
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(evaluateScroll);
      observer.observe(scrollEl);
      if (scrollEl.firstElementChild) {
        observer.observe(scrollEl.firstElementChild);
      }
    }
    return () => {
      scrollEl.removeEventListener('scroll', evaluateScroll);
      observer?.disconnect();
    };
  }, [scrollEl, evaluateScroll, visibleLogs.length]);

  // The "total" line of the receipt: entries across every merged log, counted
  // after collapsing so it matches the cards on screen.
  const totalEntries = useMemo(() => visibleLogs.reduce((sum, log) => sum + log.entries.length, 0), [visibleLogs]);

  const rootClassName = locale === 'zh' ? 'jarvis-patch-log jarvis-patch-log--zh' : 'jarvis-patch-log';
  const cardColor = themeColors.card ?? (isDark ? '#111827' : '#FFFFFF');
  const surface = withHexAlpha(cardColor, SURFACE_ALPHA);
  // `cream` has no textSecondary; fall back to body text the way ThemeContext does.
  const mutedColor = themeColors.textSecondary ?? themeColors.text;

  // Handed to the stylesheet so every dashed rule and muted label follows the
  // active palette without repeating an inline style per element.
  const themeVars = {
    '--jarvis-patch-log-rule': withHexAlpha(themeColors.text, 0.18),
    '--jarvis-patch-log-muted': mutedColor,
    '--jarvis-patch-log-tint': withHexAlpha(themeColors.text, 0.07),
    '--jarvis-patch-log-edge': withHexAlpha(themeColors.text, 0.1),
  } as CSSProperties;

  return (
    <Modal
      opened={opened}
      onClose={() => {}}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size={472}
      radius={SURFACE_RADIUS}
      zIndex={zIndex}
      // Mantine pads the modal viewport by `--modal-y-offset` (5dvh by default) and
      // caps the content at `100dvh - 2 * that`. Pinning the offset to 32px is what
      // makes the sheet height below exact instead of a guess that overshoots.
      yOffset="32px"
      trapFocus
      lockScroll
      classNames={{ content: rootClassName }}
      overlayProps={{
        color: isDark ? themeColors.background : '#111827',
        opacity: 0.24,
        blur: 2,
      }}
      styles={{
        content: {
          ...themeVars,
          backgroundColor: surface,
          color: themeColors.text,
          // The translucent sheet needs its own outline: over a light page the
          // blurred panel and the background are close enough in value that the
          // rounded edge would otherwise be hard to see.
          border: '1px solid var(--jarvis-patch-log-edge)',
          // `dvh`, and the same value for max-height: Mantine's own
          // `max-height: calc(100dvh - 2 * y-offset)` stays out of the way, so the
          // sheet can never ask for more room than the modal viewport has.
          height: 'min(718px, calc(100dvh - 64px))',
          maxHeight: 'min(718px, calc(100dvh - 64px))',
          // Inline, not only in the stylesheet: Mantine ships
          // `.mantine-Modal-content { overflow-y: auto }` at the same specificity as
          // our class, so whichever sheet loads last wins — and when Mantine wins the
          // user gets a second scrollbar next to `__scroll`.
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        body: { padding: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* No Mantine header: the receipt's own masthead carries the wordmark, title and controls. */}
      <div className="jarvis-patch-log__masthead">
        <img
          className="jarvis-patch-log__logo"
          src={isDark ? BRAND_LOGO_WHITE : BRAND_LOGO}
          alt="Apex Intelligence"
          width={746}
          height={278}
        />
        <p className="jarvis-patch-log__title">{t('title')}</p>

        <div className="jarvis-patch-log__header-actions">
          {onLocaleChange ? <PatchLogLocaleSwitcher locale={locale} onChange={onLocaleChange} /> : <span />}
          {onDefer ? (
            <Button
              type="button"
              variant="subtle"
              color="gray"
              size="compact-sm"
              onClick={onDefer}
              disabled={confirming}
              styles={{ root: { color: mutedColor, fontSize: 13, fontWeight: 500 } }}
            >
              {t('remindLater')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="jarvis-patch-log__viewport">
        {/* Focusable on purpose (axe: scrollable-region-focusable) — keyboard users must be able to scroll it. */}
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: scrollable region must be reachable by keyboard */}
        <div ref={setScrollEl} className="jarvis-patch-log__scroll" tabIndex={0} role="region" aria-label={t('title')}>
          <div className="jarvis-patch-log__content">
            {visibleLogs.map((log, logIndex) => (
              <section key={log.id} aria-label={log.versionLabel}>
                <p className="jarvis-patch-log__version">
                  {formatPatchLogVersionLabel(log.versionLabel, t('version'))}
                  {' — '}
                  {formatPatchLogDate(log.releaseDate, locale)}
                </p>
                <div className="jarvis-patch-log__entries">
                  {log.entries.map((entry, entryIndex) => (
                    <PatchLogEntryCard
                      key={entry.id}
                      entry={entry}
                      locale={locale}
                      eager={logIndex === 0 && entryIndex === 0}
                    />
                  ))}
                </div>
              </section>
            ))}

            {visibleLogs.length === 0 ? <p className="jarvis-patch-log__empty">{t('historyEmpty')}</p> : null}

            <p className="jarvis-patch-log__total">
              <span>{t('totalChanges')}</span>
              <span className="jarvis-patch-log__total-value">{totalEntries}</span>
            </p>

            {/* Confirm lives at the END of the content on purpose: reaching it means the user scrolled past everything. */}
            <div className="jarvis-patch-log__confirm-row">
              <Button
                type="button"
                color="brand"
                h={42}
                radius={6}
                fullWidth
                className="jarvis-patch-log__confirm"
                leftSection={onConfirm ? <IconCheck size={18} aria-hidden="true" /> : undefined}
                loading={confirming}
                onClick={onConfirm}
              >
                {onConfirm ? t('confirm') : t('close')}
              </Button>
            </div>
          </div>
        </div>

        {showScrollHint ? (
          <div
            className="jarvis-patch-log__hint"
            aria-hidden="true"
            style={{
              color: themeColors.text,
              background: `linear-gradient(to bottom, transparent, ${surface} 68%)`,
            }}
          >
            <IconChevronDown size={22} stroke={2.2} className="jarvis-patch-log__hint-chevron" />
            <span>{t('scrollHint')}</span>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default PatchLogModal;
